import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { color, font } from '../styles/tokens';

// Ports the prototype's page-transition "curtain". A dark panel wipes up over the
// screen, the route swaps underneath while it's covered, then it slides off the top.
//
// State cycle (mirrors the original timings exactly):
//   null  -> panel parked below the viewport (translateY(100%)), no transition
//   'in'  -> panel covers the screen (translateY(0)),  ~0.34s wipe up
//   'out' -> panel slides off the top (translateY(-100%)); route swap happens here
//   null  -> reset, ready for the next navigation
//
// Navigation is intercepted so the swap lands at the 300ms mark (screen fully covered).
// If a transition is already in flight, we just navigate immediately — no double wipe.

const TransitionContext = createContext(null);

const scrollTop = () => {
  try {
    window.scrollTo({ top: 0, behavior: 'auto' });
  } catch {
    window.scrollTo(0, 0);
  }
};

export const TransitionProvider = ({ children }) => {
  const navigate = useNavigate();
  const [curtain, setCurtain] = useState(null); // null | 'in' | 'out'
  const busy = useRef(false);
  const timers = useRef([]);

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    },
    []
  );

  const transitionNavigate = useCallback(
    (to, options) => {
      const apply = () => {
        navigate(to, options);
        scrollTop();
      };

      // Already mid-transition: apply straight away, like the prototype did.
      if (busy.current) {
        apply();
        return;
      }

      busy.current = true;
      setCurtain('in');
      timers.current.push(
        setTimeout(() => {
          apply();
          setCurtain('out');
        }, 300)
      );
      timers.current.push(
        setTimeout(() => {
          setCurtain(null);
          busy.current = false;
        }, 660)
      );
    },
    [navigate]
  );

  return (
    <TransitionContext.Provider value={{ navigate: transitionNavigate, curtain }}>
      {children}
    </TransitionContext.Provider>
  );
};

// Drop-in replacement for react-router's useNavigate that plays the curtain.
// Falls back to a plain navigate if used outside a TransitionProvider.
export const useTransitionNavigate = () => {
  const ctx = useContext(TransitionContext);
  const raw = useNavigate();
  return ctx ? ctx.navigate : raw;
};

// The full-screen overlay. Render once, inside the layout.
export const Curtain = () => {
  const ctx = useContext(TransitionContext);
  const cs = ctx ? ctx.curtain : null;

  const transform =
    cs === 'in' ? 'translateY(0)' : cs === 'out' ? 'translateY(-100%)' : 'translateY(100%)';

  return (
    <div
      aria-hidden={!cs}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 180,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 18,
        background: '#0b0f17',
        transform,
        transition: cs ? 'transform .34s cubic-bezier(.76,0,.24,1)' : 'none',
        pointerEvents: cs ? 'auto' : 'none',
      }}
    >
      <div style={{ display: 'flex', animation: 'vt-spin 2.6s linear infinite', transformOrigin: 'center' }}>
        <svg width="56" height="56" viewBox="0 0 200 200" aria-hidden="true">
          <path
            fillRule="evenodd"
            fill={color.accent}
            d="M 100.0 8.0 L 119.7 26.6 L 146.0 20.3 L 153.7 46.3 L 179.7 54.0 L 173.4 80.3 L 192.0 100.0 L 173.4 119.7 L 179.7 146.0 L 153.7 153.7 L 146.0 179.7 L 119.7 173.4 L 100.0 192.0 L 80.3 173.4 L 54.0 179.7 L 46.3 153.7 L 20.3 146.0 L 26.6 119.7 L 8.0 100.0 L 26.6 80.3 L 20.3 54.0 L 46.3 46.3 L 54.0 20.3 L 80.3 26.6 Z M 100 70 A 30 30 0 1 0 100 130 A 30 30 0 1 0 100 70 Z"
          />
          <circle cx="100" cy="100" r="22" fill={color.text} />
        </svg>
      </div>
      <span style={{ font: `500 11px ${font.mono}`, letterSpacing: '.42em', color: color.textFaint }}>
        V-TECH INDUSTRIES
      </span>
    </div>
  );
};
